/**
 * @file: leadDetailKit.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Shared visual language for the lead-detail surface: the palette and the
 * label/value Field row, so the screen and its hero card draw from one source
 * of truth instead of re-declaring colors and rows per file.
 */

import { Typography } from '@euler/app/components/typography';
import { StatusColors } from '@euler/components/Colors';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LeadIntention, LeadIntentionLabel } from '@euler/model/lead/Lead';
import { dateFromValue } from '@euler/utils/datetime';
import format from 'date-fns/format';
import { ChevronRight, Pencil } from 'lucide-react-native';
import { memo } from 'react';
import { TouchableOpacity, View, ViewStyle } from 'react-native';

// A cool "instrument panel" palette. Ink-on-mist greys form the chassis, a
// single brand blue carries every action, and amber / red are held in reserve
// for time pressure — the one thing a follow-up cockpit must always surface.
export const LD = {
  canvas: '#F4F6FA',
  surface: '#FFFFFF',
  hairline: '#E8ECF3',
  ink: '#16233B',
  steel: '#475A75',
  mist: '#93A1B5',
  brand: '#0066FF',
  brandSoft: '#EAF3FF',
  amber: '#B45309',
  amberSoft: '#FEF3C7',
  danger: '#C0362C',
  dangerSoft: '#FCE9E7',
  success: StatusColors.Success,
  successSoft: '#E1F6EB',
} as const;

// Soft elevation shared by every card on the surface, so the lead-info hero and
// the secondary cards stay visually identical (no borders, one shadow).
export const cardShadowStyle = (rpx: (n: number) => number): ViewStyle => ({
  shadowColor: '#1B2A44',
  shadowOpacity: 0.06,
  shadowRadius: rpx(14),
  shadowOffset: { width: 0, height: rpx(4) },
  elevation: 2,
});

export const formatDate = (value: string | Date | undefined | null): string => {
  if (!value) return '—';
  try {
    return format(dateFromValue(value), 'yyyy-MM-dd');
  } catch {
    return '—';
  }
};

export const Field = ({
  label,
  value,
  divider = true,
  onPress,
  affordance = 'pencil',
  emptyText = '—',
}: {
  label: string;
  value: string | null | undefined;
  // A hairline separator above the row; pass false for the first row of a card.
  divider?: boolean;
  // When set, the row is tappable (edit affordance) and shows an edit icon.
  onPress?: () => void;
  // The tap affordance icon: a pencil (inline edit) or a chevron (drills into a
  // picker / another surface). Only shown when `onPress` is set.
  affordance?: 'pencil' | 'chevron';
  // Placeholder shown (grayed) when the value is empty.
  emptyText?: string;
}) => {
  const { rpx } = useResponsiveContext();
  const isEmpty = !value;
  const style: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: rpx(44),
    borderTopWidth: divider ? 1 : 0,
    borderTopColor: LD.hairline,
  };
  const body = (
    <>
      <Typography.Text
        size={rpx(13)}
        color={LD.steel}
        style={{ marginRight: rpx(12) }}
      >
        {label}
      </Typography.Text>
      <Typography.Text
        size={rpx(14)}
        color={isEmpty ? LD.mist : LD.ink}
        bold={!isEmpty || undefined}
        style={{ flex: 1, textAlign: 'right' }}
      >
        {value || emptyText}
      </Typography.Text>
      {onPress ? (
        affordance === 'chevron' ? (
          <ChevronRight
            size={rpx(16)}
            color={LD.mist}
            style={{ marginLeft: rpx(4) }}
          />
        ) : (
          <Pencil
            size={rpx(15)}
            color={LD.brand}
            style={{ marginLeft: rpx(8) }}
          />
        )
      ) : null}
    </>
  );
  return onPress ? (
    <TouchableOpacity activeOpacity={0.6} onPress={onPress} style={style}>
      {body}
    </TouchableOpacity>
  ) : (
    <View style={style}>{body}</View>
  );
};

// Visit-intention chip colors — a calm green→red read, neutral grey for the
// unset `undetermined`. Shared by the detail header and the list card.
export const intentionTone = (
  intention: LeadIntention,
): { bg: string; fg: string } => {
  switch (intention) {
    case 'high':
      return { bg: LD.successSoft, fg: LD.success };
    case 'medium':
      return { bg: LD.brandSoft, fg: LD.brand };
    case 'low':
      return { bg: LD.amberSoft, fg: LD.amber };
    case 'none':
      return { bg: LD.dangerSoft, fg: LD.danger };
    case 'undetermined':
      return { bg: LD.hairline, fg: LD.mist };
  }
};

/** A small colored 到店意向 pill (label + tone). */
export const IntentionChip = memo(
  ({ intention }: { intention: LeadIntention }) => {
    const { rpx } = useResponsiveContext();
    const tone = intentionTone(intention);
    return (
      <View
        style={{
          backgroundColor: tone.bg,
          borderRadius: rpx(8),
          paddingHorizontal: rpx(8),
          paddingVertical: rpx(3),
        }}
      >
        <Typography.Text size={rpx(12)} color={tone.fg} medium>
          {LeadIntentionLabel[intention]}
        </Typography.Text>
      </View>
    );
  },
);
